// Bulk Actions Component

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateLeadsBulk, deleteLead } from '@/lib/api';
import type { LeadStatus } from '@/lib/types';
import { statusColors } from '@/lib/types';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import toast from 'react-hot-toast';

interface BulkActionsProps {
  selectedIds: string[];
  onClearSelection: () => void;
}

export default function BulkActions({ selectedIds, onClearSelection }: BulkActionsProps) {
  const queryClient = useQueryClient();
  const [selectedAction, setSelectedAction] = useState<string>('');

  const updateMutation = useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: LeadStatus }) =>
      updateLeadsBulk(ids, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success(`${selectedIds.length} Leads erfolgreich aktualisiert`);
      onClearSelection();
      setSelectedAction('');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Fehler beim Bulk-Update');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      // Lösche alle Leads nacheinander
      for (const id of ids) {
        await deleteLead(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success(`${selectedIds.length} Lead(s) erfolgreich gelöscht`);
      onClearSelection();
      setSelectedAction('');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Fehler beim Löschen');
    },
  });

  const handleApply = () => {
    if (!selectedAction) {
      toast.error('Bitte wählen Sie eine Aktion aus');
      return;
    }

    if (selectedIds.length === 0) {
      toast.error('Keine Leads ausgewählt');
      return;
    }

    // Löschen
    if (selectedAction === 'delete') {
      if (
        !window.confirm(
          `Möchten Sie ${selectedIds.length} Lead(s) wirklich unwiderruflich löschen?`
        )
      ) {
        return;
      }
      deleteMutation.mutate(selectedIds);
      return;
    }

    // Status-Update
    if (
      selectedAction === 'archived' &&
      !window.confirm(
        `Möchten Sie ${selectedIds.length} Lead(s) wirklich archivieren?`
      )
    ) {
      return;
    }

    updateMutation.mutate({
      ids: selectedIds,
      status: selectedAction as LeadStatus,
    });
  };

  if (selectedIds.length === 0) return null;

  return (
    <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex items-center justify-between">
      <div className="flex items-center space-x-4">
        <span className="text-sm font-medium text-primary-900">
          {selectedIds.length} {selectedIds.length === 1 ? 'Lead' : 'Leads'} ausgewählt
        </span>
        
        <Select
          options={[
            { value: '', label: 'Aktion wählen...' },
            { value: 'new', label: `Status → ${statusColors.new.label}` },
            { value: 'contacted', label: `Status → ${statusColors.contacted.label}` },
            {
              value: 'in_progress',
              label: `Status → ${statusColors.in_progress.label}`,
            },
            { value: 'converted', label: `Status → ${statusColors.converted.label}` },
            { value: 'archived', label: `Status → ${statusColors.archived.label}` },
            { value: 'delete', label: '🗑️ Löschen' },
          ]}
          value={selectedAction}
          onChange={(e) => setSelectedAction(e.target.value)}
          className="w-64"
        />
        
        <Button
          size="sm"
          onClick={handleApply}
          loading={updateMutation.isPending || deleteMutation.isPending}
          disabled={!selectedAction}
        >
          Anwenden
        </Button>
      </div>

      <Button variant="ghost" size="sm" onClick={onClearSelection}>
        Auswahl aufheben
      </Button>
    </div>
  );
}
